import {
  ArrowFatRightIcon,
  ArrowsClockwiseIcon,
  ChatCircleIcon,
  FilmSlateIcon,
  ImageIcon,
  LockSimpleIcon,
  MagicWandIcon,
  PaletteIcon,
  ShoppingCartIcon,
  SparkleIcon,
  SunIcon,
  TShirtIcon,
  VideoCameraIcon,
} from '@phosphor-icons/react';
import { type ReactNode } from 'react';

import { Spinner } from '@/components/ui/spinner';
import {
  type ArtifactKind,
  NEXT_STEPS,
  type NextStep,
  resolveChatPrompt,
} from '@/lib/ai-agent/next-steps';
import { QUIET_ACTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/** Chip ids that a color lock disables (recolor targets brand colors). */
const COLOR_LOCK_CHIP_IDS = new Set(['recolor']);

/**
 * Shared pill className for durable workflow actions that can appear below
 * the media composer (for example, Approve & schedule). Prompt-backed AI
 * starters use QUIET_ACTION below so they read as composer aids rather than
 * competing primary actions.
 *
 * h-8 fixed at EVERY breakpoint (not h-11 on mobile) — the action rail stays
 * compact beside the composer. One-step AI transformations use QUIET_ACTION
 * below; only durable workflow actions such as Shopify keep this pill grammar.
 */
export const ACTION_PILL_CLASS =
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border/80 bg-transparent px-3.5 text-caption font-normal text-muted-foreground/80 transition-colors hover:bg-muted/30 hover:text-foreground active:bg-muted/40 dark:hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [scroll-margin-inline:16px]';

export type SuggestionChipAction = 'prefill' | 'auto-send' | 'named-action';
export type SuggestionChipNamedAction =
  | 'animate'
  | 'upscale'
  | 'orbit'
  | 'push-shopify'
  | 'extend'
  | 'caption'
  | 'remix-background'
  | 'recolor';

export interface SuggestionChip {
  id: string;
  label: string;
  icon?: ReactNode;
  action: SuggestionChipAction;
  /** Auto-send / prefill chips put this string in the chat composer. */
  prompt?: string;
  namedAction?: SuggestionChipNamedAction;
}

// size-4 (not size-3.5) to match the icon size used by the dialog's own
// pills (Create a post / Make video / Add to project).
const ICON_CLS = 'shrink-0 [&>svg]:size-4';

// icon-per-id — the registry (`next-steps.ts`) owns label/prompt/action, but
// has no notion of iconography, so this is the one place the chip's visual
// identity is chosen. Keys are `NextStep.id`s from the `image`/`video` kinds.
const CHIP_ICONS: Record<string, ReactNode> = {
  animate: <MagicWandIcon />,
  'remix-background': <ImageIcon />,
  recolor: <PaletteIcon />,
  'lifestyle-staging': <TShirtIcon />,
  variation: <ArrowsClockwiseIcon />,
  'write-caption': <ChatCircleIcon />,
  'push-shopify': <ShoppingCartIcon />,
  extend: <ArrowFatRightIcon />,
  'orbit-camera': <SparkleIcon />,
  'slow-motion': <VideoCameraIcon />,
  'golden-hour': <SunIcon />,
  cinematic: <FilmSlateIcon />,
};

// Derived from NEXT_STEPS (the chat-turn + dialog shared registry) so the two
// surfaces can never drift apart again — see `next-steps.ts` header comment.
// `{url}` is stripped rather than interpolated: the dialog already operates
// on the currently-open asset, so a chip's `prompt` must never carry a
// literal "{url}" placeholder into the composer.
function chipFromNextStep(step: NextStep): SuggestionChip {
  return {
    id: step.id,
    label: step.label,
    icon: CHIP_ICONS[step.id],
    action: step.namedAction ? 'named-action' : 'auto-send',
    // Also covers remix-background, whose handler is otherwise consumed by
    // iterateInPlace('background-replacement') — without a prompt the
    // handler called undefined.trim() and threw (dead chip).
    prompt: resolveChatPrompt(step),
    namedAction: step.namedAction as SuggestionChipNamedAction | undefined,
  };
}

const MEDIA_STUDIO_CHIP_KINDS: Array<Extract<ArtifactKind, 'image' | 'video'>> = ['image', 'video'];

export const ICP_CHIPS: Record<'image' | 'video', SuggestionChip[]> = Object.fromEntries(
  MEDIA_STUDIO_CHIP_KINDS.map((kind) => [kind, NEXT_STEPS[kind].map(chipFromNextStep)]),
) as Record<'image' | 'video', SuggestionChip[]>;

export function SuggestionChipRow({
  kind,
  onAction,
  onPrefill,
  composerMode = false,
  disabled = false,
  activeId = null,
  colorLocked = false,
  hideNamedActions,
}: {
  kind: 'image' | 'video';
  hideNamedActions?: readonly SuggestionChipNamedAction[];
  onAction: (chip: SuggestionChip, event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Composer mode drafts safe edits visibly instead of firing generation. */
  onPrefill?: (chip: SuggestionChip) => void;
  /** Hides duplicate workflow actions and routes prompt-backed AI edits to the composer. */
  composerMode?: boolean;
  disabled?: boolean;
  activeId?: string | null;
  /** BI-5-03: a brand color (primary/secondary/accent) is INHERITED-locked —
   *  disable the recolor chip with a lock affordance. Presentational only; the
   *  caller resolves the inherited lock set. Empty locks → false → no change. */
  colorLocked?: boolean;
}) {
  // Push-to-Shopify chip is intentionally ALWAYS surfaced — even when the
  // backend flag is OFF. The click resolves to a `<ConnectShopifySheet>`
  // empty-state that links into Settings → Integrations. This converts intent
  // into a measurable signal (`push_to_shopify_intent` event) without shipping
  // a half-broken backend (DX advisor #1 blocker). When the backend lands the
  // chip's handler swaps from "open empty-state" to "perform real push."
  const baseChips = composerMode
    ? ICP_CHIPS[kind].filter((chip) => chip.namedAction !== 'push-shopify')
    : ICP_CHIPS[kind];
  const chips = hideNamedActions
    ? baseChips.filter((chip) => !chip.namedAction || !hideNamedActions.includes(chip.namedAction))
    : baseChips;

  // No own padding/scroll/wrap here — this renders inline inside
  // MediaStudioDialog's single scrolling action-pill row (alongside the
  // Suggest chip and Add-to-project), which already owns the horizontal
  // scroll + edge-fade mask. A second, independently-wrapping scroll
  // container here (the old standalone-row styling) fought that parent and
  // wrapped to 2-3 rows at sm:+ widths — the pill row reading "really tall"
  // on tablet/iPad (caught in live review). gap-1.5 (not gap-2) matches the
  // outer row's own gap so the Suggest→first-chip spacing reads identically
  // to chip→chip spacing.
  return (
    <div className="flex flex-nowrap items-center gap-1.5">
      {chips.map((chip) => {
        const isActive = chip.id === activeId;
        // A color-locked recolor chip is a hard, standing disable (lock
        // affordance), distinct from the transient "working" disable.
        const isLockDisabled = colorLocked && COLOR_LOCK_CHIP_IDS.has(chip.id);
        const isDisabled = (disabled && !isActive) || isLockDisabled;
        const tooltipTitle = isLockDisabled
          ? 'Locked by your brand kit'
          : disabled && !isActive
            ? 'One action at a time'
            : undefined;
        const isWorkflowAction = chip.namedAction === 'push-shopify';
        const isAiAction = !isWorkflowAction;
        return (
          <button
            key={chip.id}
            type="button"
            disabled={isDisabled}
            aria-disabled={isDisabled || undefined}
            className={cn(
              isAiAction ? QUIET_ACTION : ACTION_PILL_CLASS,
              isAiAction && 'h-8 shrink-0 whitespace-nowrap px-1.5 text-caption font-normal',
            )}
            onClick={(e) => {
              if (isDisabled) return;
              if (composerMode && chip.namedAction !== 'animate' && chip.prompt && onPrefill) {
                onPrefill(chip);
                return;
              }
              onAction(chip, e);
            }}
            title={tooltipTitle}
          >
            {isLockDisabled ? (
              <span className={ICON_CLS}>
                <LockSimpleIcon weight="regular" />
              </span>
            ) : chip.icon && (!isAiAction || (composerMode && chip.namedAction === 'animate')) ? (
              <span className={ICON_CLS}>{chip.icon}</span>
            ) : null}
            {chip.label}
            {isActive ? <Spinner size="sm" className="text-skill" /> : null}
          </button>
        );
      })}
    </div>
  );
}
